import { useLingui } from "@lingui/react/macro";
import type { WorkspaceAppId } from "@rakazo/contracts";
import { WORKSPACE_APPS } from "@rakazo/contracts";
import {
  BotAvatar,
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@rakazo/ui-web";
import { ArrowLeft, Check, Grid2X2, Home, MessageCircle, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";
import { createSpaceRpc, selectedSpaceId } from "../lib/rpc";
import { useWorkspace } from "../lib/use-workspace";
import { useEcho } from "./EchoProvider";
import { WorkspaceAppIcon } from "./WorkspaceAppIcon";
import { WorkspaceControls } from "./WorkspaceControls";
import { WorkspaceNotifications } from "./WorkspaceNotifications";

const favorites: WorkspaceAppId[] = [
  "messages",
  "tasks",
  "missions",
  "files",
  "editor",
  "computers",
];

/** A view of the session's existing apps, never a second app lifecycle. */
export type DockWindow = {
  id: string;
  app: WorkspaceAppId;
  name: string;
  path: string;
  minimized?: boolean;
};

export function WorkspaceDock({
  windows,
  active,
  onOpen,
  onActivate,
  onNewWindow,
  onHome,
  onLauncher,
}: {
  windows: DockWindow[];
  /** The window in front, so the dock can mark its app and resume from its address. */
  active?: string;
  onOpen: (path: string) => void;
  onActivate: (id: string) => void;
  onNewWindow: (path: string) => void;
  onHome: () => void;
  onLauncher: () => void;
}) {
  const { t } = useLingui();
  const echo = useEcho();
  const [choosing, setChoosing] = useState<WorkspaceAppId | null>(null);
  // Pointer focus and Show Desktop do not change the address bar. Use the actual
  // foreground window as Echo's return destination, rather than the last navigation.
  const activeWindow = windows.find((window) => window.id === active);
  const activePath = activeWindow?.path ?? "/app";
  const returnToWork =
    activePath.split("?")[0] === `/app/${echo.bot?.id}` ? echo.returnToWork : null;
  // Agent work is its own real state. It is reported against Activity, which owns these records,
  // and never inferred from an app having a window open.
  const client = useMemo(() => createSpaceRpc(selectedSpaceId()), []);
  const activity = useWorkspace(
    useCallback(() => client.workspace.activity({ filter: "active" }), [client]),
    30000,
  );
  const running = (activity.data?.counts.working ?? 0) + (activity.data?.counts.queued ?? 0);
  const needsYou = activity.data?.counts.attention ?? 0;
  // One slot per app, holding however many windows that app has open.
  const openApps = [...new Set(windows.map((window) => window.app))].filter(
    (app) => app !== "home" && app !== "notifications",
  );
  const entries = [...favorites, ...openApps.filter((app) => !favorites.includes(app))].map(
    (app) => ({
      app,
      name:
        WORKSPACE_APPS.find((item) => item.id === app)?.name ??
        (app === "shared-bots" ? t`Shared bots` : app === "apps" ? t`Apps` : app),
      path:
        WORKSPACE_APPS.find((item) => item.id === app)?.web ??
        (app === "shared-bots"
          ? "/app/shared-bots"
          : app === "apps"
            ? "/app/workspace/apps"
            : "/app"),
      group: windows.filter((window) => window.app === app),
    }),
  );
  return (
    <div className="workspace-dock-area">
      <nav aria-label={t`App dock`} className="workspace-dock">
        <button
          type="button"
          aria-label={t`Show desktop`}
          title={t`Show desktop`}
          aria-current={active === "home" ? "page" : undefined}
          onClick={onHome}
        >
          <Home size={19} />
        </button>
        <button
          type="button"
          aria-label={t`Switch apps`}
          title={t`Switch apps (Alt + J)`}
          onClick={onLauncher}
        >
          <Grid2X2 size={19} />
        </button>
        <span className="workspace-dock-divider" aria-hidden="true" />
        <div className="workspace-dock-apps">
          {entries.map((entry) => {
            const open = entry.group.length > 0;
            const only = entry.group.length === 1 ? entry.group[0] : undefined;
            const mine = activeWindow?.app === entry.app;
            const marks = (
              <>
                <WorkspaceAppIcon id={entry.app} compact />
                <span className="workspace-dock-name">{entry.name}</span>
                <span
                  className="workspace-dock-indicator"
                  data-multiple={entry.group.length > 1 || undefined}
                  aria-hidden="true"
                />
                {entry.app === "activity" && needsYou > 0 ? (
                  <span
                    role="img"
                    className="workspace-notification-badge"
                    aria-label={t`${needsYou} need you`}
                  >
                    {needsYou}
                  </span>
                ) : entry.app === "activity" && running > 0 ? (
                  <span
                    className="workspace-dock-working"
                    aria-label={t`${running} working`}
                    role="img"
                  />
                ) : null}
              </>
            );
            const context = (trigger: ReactNode) => (
              <ContextMenu key={entry.app}>
                <ContextMenuTrigger render={<div className="flex shrink-0" />}>
                  {trigger}
                </ContextMenuTrigger>
                <ContextMenuContent aria-label={t`${entry.name} actions`} className="min-w-60">
                  {entry.group.map((window) => (
                    <ContextMenuItem key={window.id} onClick={() => onActivate(window.id)}>
                      <span className="min-w-0 flex-1 truncate">{window.name}</span>
                      {window.minimized ? (
                        <span className="text-xs text-muted-foreground">{t`Minimized`}</span>
                      ) : null}
                    </ContextMenuItem>
                  ))}
                  {entry.group.length > 0 && <ContextMenuSeparator />}
                  <ContextMenuItem onClick={() => onNewWindow(entry.path)}>
                    <Plus />
                    {t`New window`}
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            );
            // Several windows of one app need choosing between, so the slot opens a list of them
            // by name. One window, or none, stays a single click.
            if (entry.group.length > 1)
              return context(
                <Popover
                  key={entry.app}
                  open={choosing === entry.app}
                  onOpenChange={(open) => setChoosing(open ? entry.app : null)}
                >
                  <PopoverTrigger
                    aria-label={t`${entry.name} windows`}
                    title={entry.name}
                    data-open
                    data-minimized={entry.group.every((window) => window.minimized) || undefined}
                    aria-current={mine ? "page" : undefined}
                  >
                    {marks}
                  </PopoverTrigger>
                  <PopoverContent
                    side="top"
                    align="center"
                    className="w-80 max-w-[calc(100vw-2rem)] gap-0 p-2"
                  >
                    <div className="px-3 py-2 text-sm font-medium">{entry.name}</div>
                    <div className="max-h-72 overflow-y-auto">
                      {entry.group.map((window) => (
                        <button
                          key={window.id}
                          type="button"
                          className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
                          aria-current={window.id === active ? "page" : undefined}
                          onClick={() => {
                            onActivate(window.id);
                            setChoosing(null);
                          }}
                        >
                          <WorkspaceAppIcon id={entry.app} compact />
                          <span className="min-w-0 flex-1 truncate" title={window.name}>
                            {window.name}
                          </span>
                          {window.id === active ? (
                            <Check size={16} aria-label={t`Current window`} />
                          ) : null}
                          {window.minimized ? (
                            <span className="text-xs text-muted-foreground">{t`Minimized`}</span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 border-t pt-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-3 px-3"
                        onClick={() => {
                          setChoosing(null);
                          onNewWindow(entry.path);
                        }}
                      >
                        <Plus size={16} />
                        {t`New window`}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>,
              );
            return context(
              <button
                type="button"
                key={entry.app}
                // The name stays the app's own; being put away is a state, not a different control.
                aria-label={entry.name}
                aria-current={mine ? "page" : undefined}
                title={
                  only?.minimized ? `${only.name} — ${t`Minimized`}` : (only?.name ?? entry.name)
                }
                data-open={open || undefined}
                data-minimized={only?.minimized || undefined}
                onClick={() => {
                  // Restore the instance without routing through another window of the same app.
                  if (only) onActivate(only.id);
                  else onOpen(entry.path);
                }}
              >
                {marks}
              </button>,
            );
          })}
        </div>
        <span className="workspace-dock-divider" aria-hidden="true" />
        <WorkspaceControls onOpen={onOpen} resumePath={activePath} />
        <WorkspaceNotifications />
        {echo.error ? (
          <Popover>
            <PopoverTrigger aria-label={t`Assistant unavailable`} title={t`Assistant unavailable`}>
              <MessageCircle size={21} className="text-destructive" />
            </PopoverTrigger>
            <PopoverContent side="top" align="end" className="max-w-72">
              <p role="alert" className="text-xs">
                {echo.error}
              </p>
              <Button size="sm" onClick={echo.refresh}>{t`Retry`}</Button>
            </PopoverContent>
          </Popover>
        ) : (
          <button
            type="button"
            disabled={!echo.bot}
            aria-label={returnToWork ? t`Return to work` : `${t`Ask`} ${echo.bot?.name ?? "Echo"}`}
            title={returnToWork ? t`Return to work` : `${t`Ask`} ${echo.bot?.name ?? "Echo"}`}
            onClick={returnToWork ?? (() => echo.open(activePath, activeWindow?.id))}
          >
            {returnToWork ? (
              <ArrowLeft size={21} />
            ) : echo.bot ? (
              <BotAvatar color={echo.bot.color} identity={echo.bot.id} size={26} />
            ) : (
              <MessageCircle size={21} />
            )}
          </button>
        )}
      </nav>
    </div>
  );
}
